import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import {
  Loader2, Shield, Filter, Eye, User, FileText, Calendar, Clock, Activity,
  TrendingUp, Users, Truck, DollarSign, UserCheck, UserX, Edit, Trash2,
  CheckCircle, XCircle, AlertTriangle, Plus, Lock, Unlock,
} from 'lucide-react';
import { cn, localToday, localDaysAgo } from '@/lib/utils';

const ACTION_COLORS = {
  create: 'bg-emerald-500/15 text-emerald-500 border-emerald-500/20',
  update: 'bg-blue-500/15 text-blue-500 border-blue-500/20',
  delete: 'bg-red-500/15 text-red-500 border-red-500/20',
  status_change: 'bg-amber-500/15 text-amber-500 border-amber-500/20',
  approve: 'bg-emerald-500/15 text-emerald-500 border-emerald-500/20',
  rate_change: 'bg-violet-500/15 text-violet-500 border-violet-500/20',
};

const ACTION_ICONS = {
  create: Plus,
  update: Edit,
  delete: Trash2,
  status_change: AlertTriangle,
  approve: CheckCircle,
  rate_change: DollarSign,
};

const ENTITY_ICONS = {
  order: FileText,
  worker: UserCheck,
  client: User,
  payroll: DollarSign,
  scheduling: Calendar,
  user: Users,
  time_submission: Clock,
};

const ROLE_LABELS = { admin: 'Админ', manager: 'Менеджер', worker: 'Работник' };

function StatCard({ icon: Icon, value, label, color }) {
  return (
    <Card className="cgc-card">
      <CardContent className="p-4">
        <div className="flex items-center gap-3">
          <div className={cn("w-10 h-10 rounded-lg flex items-center justify-center", color)}>
            <Icon className="h-5 w-5" />
          </div>
          <div>
            <p className="text-2xl font-bold tabular-nums">{value}</p>
            <p className="text-xs text-muted-foreground">{label}</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default function AuditPage() {
  const { api, user } = useAuth();
  const { t, lang } = useLanguage();
  const [loading, setLoading] = useState(true);
  const [events, setEvents] = useState([]);
  const [stats, setStats] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [auditUsers, setAuditUsers] = useState([]);

  const formatDateTime = (isoString) => {
    if (!isoString) return '-';
    const date = new Date(isoString);
    return date.toLocaleString(lang === 'en' ? 'en-US' : 'ru-RU', {
      day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit',
    });
  };

  // Filters
  const [entityType, setEntityType] = useState('all');
  const [action, setAction] = useState('all');
  const [userId, setUserId] = useState('all');
  const [dateFrom, setDateFrom] = useState(() => localDaysAgo(7));
  const [dateTo, setDateTo] = useState(localToday());

  // Load audit users for filter
  useEffect(() => {
    api.get('/audit/users').then(r => setAuditUsers(r.data || [])).catch(() => {});
  }, [api]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      let url = `/audit/events?date_from=${dateFrom}&date_to=${dateTo}&limit=200`;
      if (entityType !== 'all') url += `&entity_type=${entityType}`;
      if (action !== 'all') url += `&action=${action}`;
      if (userId !== 'all') url += `&user_id=${userId}`;

      const [eventsRes, statsRes] = await Promise.all([
        api.get(url),
        api.get(`/audit/stats?date_from=${dateFrom}&date_to=${dateTo}${userId !== 'all' ? '&user_id=' + userId : ''}`)
      ]);

      setEvents(eventsRes.data);
      setStats(statsRes.data);
    } catch (err) {
      console.error('Failed to fetch audit data:', err);
    } finally {
      setLoading(false);
    }
  }, [api, entityType, action, userId, dateFrom, dateTo]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleViewDetails = async (event) => {
    try {
      const res = await api.get(`/audit/events/${event.id}`);
      setSelectedEvent(res.data);
    } catch {
      setSelectedEvent(event);
    }
    setDetailOpen(true);
  };

  if (user?.role !== 'admin' && user?.role !== 'manager') {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <Shield className="h-16 w-16 mx-auto text-muted-foreground/30 mb-4" />
          <p className="text-muted-foreground">{t('audit.accessDenied')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-20">
      {/* Header */}
      <div>
        <h1 className="text-2xl md:text-3xl font-bold text-foreground">{t('audit.title')}</h1>
        <p className="text-muted-foreground mt-1">{t('audit.subtitle')}</p>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard icon={Activity} value={stats.total_events} label="Всего событий" color="bg-accent/10 text-accent" />
          <StatCard icon={Plus} value={stats.by_action?.create || 0} label="Создано" color="bg-emerald-500/10 text-emerald-500" />
          <StatCard icon={Edit} value={stats.by_action?.update || 0} label="Обновлено" color="bg-blue-500/10 text-blue-500" />
          <StatCard icon={Users} value={stats.top_users?.length || 0} label="Пользователей" color="bg-purple-500/10 text-purple-500" />
        </div>
      )}

      {/* Filters */}
      <Card className="cgc-card">
        <CardContent className="p-4">
          <div className="flex flex-wrap items-end gap-3">
            {/* User filter */}
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Сотрудник</Label>
              <Select value={userId} onValueChange={setUserId}>
                <SelectTrigger className="w-[180px]" data-testid="audit-user-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Все</SelectItem>
                  {auditUsers.map(u => (
                    <SelectItem key={u.user_id} value={u.user_id}>
                      {u.user_name} ({ROLE_LABELS[u.user_role] || u.user_role})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {/* Entity type filter */}
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Объект</Label>
              <Select value={entityType} onValueChange={setEntityType}>
                <SelectTrigger className="w-[140px]" data-testid="audit-entity-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Все</SelectItem>
                  <SelectItem value="order">Заказы</SelectItem>
                  <SelectItem value="client">Клиенты</SelectItem>
                  <SelectItem value="worker">Работники</SelectItem>
                  <SelectItem value="payroll">Зарплата</SelectItem>
                  <SelectItem value="scheduling">Расписание</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {/* Action filter */}
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Действие</Label>
              <Select value={action} onValueChange={setAction}>
                <SelectTrigger className="w-[140px]" data-testid="audit-action-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Все</SelectItem>
                  <SelectItem value="create">Создание</SelectItem>
                  <SelectItem value="update">Изменение</SelectItem>
                  <SelectItem value="delete">Удаление</SelectItem>
                  <SelectItem value="approve">Утверждение</SelectItem>
                  <SelectItem value="status_change">Смена статуса</SelectItem>
                  <SelectItem value="rate_change">Смена ставки</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {/* Date filters */}
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">С</Label>
              <Input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="w-[140px]" data-testid="audit-date-from" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">По</Label>
              <Input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className="w-[140px]" data-testid="audit-date-to" />
            </div>
            <Button variant="outline" onClick={fetchData} disabled={loading} data-testid="audit-filter-btn">
              <Filter className="h-4 w-4 mr-2" />Фильтр
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Events List — Human-readable */}
      <Card className="cgc-card">
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-lg">
            <Shield className="h-5 w-5" />
            Журнал действий
            <Badge variant="outline" className="ml-2">{events.length}</Badge>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-accent" />
            </div>
          ) : events.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <Shield className="h-12 w-12 mx-auto mb-3 opacity-30" />
              <p>Нет событий за выбранный период</p>
            </div>
          ) : (
            <div className="divide-y divide-border">
              {events.map((event) => {
                const ActionIcon = ACTION_ICONS[event.action] || Activity;
                const EntityIcon = ENTITY_ICONS[event.entity_type] || Activity;
                return (
                  <div key={event.id} className="flex items-start gap-3 px-4 py-3 hover:bg-muted/30 transition-colors"
                    data-testid={`audit-event-${event.id}`}>
                    {/* Action icon */}
                    <div className={cn("w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5",
                      ACTION_COLORS[event.action] || 'bg-muted text-muted-foreground')}>
                      <ActionIcon className="h-4 w-4" />
                    </div>
                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm leading-relaxed" data-testid={`audit-desc-${event.id}`}>
                        <span className="font-semibold">{event.user_name}</span>
                        <Badge variant="outline" className="text-[10px] mx-1.5 px-1 py-0">{ROLE_LABELS[event.user_role] || event.user_role}</Badge>
                        <span className="text-muted-foreground">{event.description || `${event.action} ${event.entity_type}`}</span>
                      </p>
                      <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground">
                        <span className="flex items-center gap-1"><Clock className="h-3 w-3" />{formatDateTime(event.timestamp)}</span>
                        <span className="flex items-center gap-1"><EntityIcon className="h-3 w-3" />{event.entity_id?.slice(0, 8)}</span>
                      </div>
                    </div>
                    {/* View details */}
                    <Button variant="ghost" size="sm" className="h-7 w-7 p-0 flex-shrink-0"
                      onClick={() => handleViewDetails(event)} data-testid={`view-audit-${event.id}`}>
                      <Eye className="h-3.5 w-3.5 text-muted-foreground" />
                    </Button>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Detail Dialog */}
      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Shield className="h-5 w-5" /> Детали события
            </DialogTitle>
          </DialogHeader>
          {selectedEvent && (
            <div className="space-y-4">
              {/* Description */}
              <div className="p-3 rounded-lg bg-muted/50 text-sm">
                <p><span className="font-semibold">{selectedEvent.user_name}</span> — {selectedEvent.description}</p>
              </div>
              {/* Metadata */}
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <Label className="text-xs text-muted-foreground">Время</Label>
                  <p className="font-mono text-xs">{formatDateTime(selectedEvent.timestamp)}</p>
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Роль</Label>
                  <p>{ROLE_LABELS[selectedEvent.user_role] || selectedEvent.user_role}</p>
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Объект</Label>
                  <p>{selectedEvent.entity_type}</p>
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">ID</Label>
                  <p className="font-mono text-xs truncate">{selectedEvent.entity_id}</p>
                </div>
              </div>
              {/* Old / New values */}
              {selectedEvent.old_value && (
                <div>
                  <Label className="text-xs text-muted-foreground">Было</Label>
                  <pre className="text-xs bg-red-500/5 border border-red-500/10 p-2 rounded overflow-auto max-h-24">
                    {JSON.stringify(selectedEvent.old_value, null, 2)}
                  </pre>
                </div>
              )}
              {selectedEvent.new_value && (
                <div>
                  <Label className="text-xs text-muted-foreground">Стало</Label>
                  <pre className="text-xs bg-emerald-500/5 border border-emerald-500/10 p-2 rounded overflow-auto max-h-24">
                    {JSON.stringify(selectedEvent.new_value, null, 2)}
                  </pre>
                </div>
              )}
              {/* Raw details (collapsed) */}
              {selectedEvent.details && (
                <details className="text-xs">
                  <summary className="cursor-pointer text-muted-foreground hover:text-foreground">Технические подробности</summary>
                  <pre className="mt-1 bg-muted p-2 rounded overflow-auto max-h-32">
                    {JSON.stringify(selectedEvent.details, null, 2)}
                  </pre>
                </details>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
